import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import Message from '../Message/Message';
import Date from '../Message/Date';
import {
  ChatMessageStatus,
  IChatMessage,
  ICoachMessagesPyalod,
  MessageUpdateType,
  SenderType,
  clearChatMessages,
  getChatMessages,
  setHasMoreMessages,
  setMessagesSkip,
  updateMessageStatus,
} from '../../../redux/chat/chatSlice';
import { useAppSelector } from '../../../redux/hooks';
import SendMessage from './SendMessage';
import LoadingMessage from '../Message/LoadingMessage';
import { IClientNameListItem } from '../../../types/client/IClientNameListItem';

export interface IChatBoxProps {
  client: IClientNameListItem;
  data?: ICoachMessagesPyalod;
  isClientTab?: boolean;
}

function ChatBox({ client, data, isClientTab }: IChatBoxProps) {
  const [rowCount, setRowCount] = useState(1); // State to store the row count
  const dispatch = useDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const {
    chatMessages,
    sentMessage,
    totalMessageCount,
    getChatMessagesLoading,
    sendMessageLoading,
    hasMore,
    skip,
  } = useAppSelector((state) => state.chat);

  useEffect(() => {
    setMessages(chatMessages);
  }, [chatMessages]);

  useEffect(() => {
    if (coachId) {
      dispatch(getChatMessages({ coachId, clientId: client.id, skip: 0, take: 10 }));
    }
  }, [coachId, client]);

  useEffect(() => {
    if (sentMessage.message) {
      setMessages([sentMessage.message, ...messages]);
    }
  }, [sentMessage]);

  useEffect(() => {
    if (data) {
      if (client.id === data.coachMessages.clientId) {
        if (data.coachMessages.messageUpdateType !== MessageUpdateType.STATUS_UPDATE) {
          setMessages([data?.coachMessages?.chatMessages[0], ...messages]);
          dispatch(
            updateMessageStatus({
              messageStatusInput: ChatMessageStatus.SEEN,
              chatMessagesId: data.coachMessages.chatMessages[0].id,
            })
          );
        } else {
          const updatedMessage = data.coachMessages.chatMessages[0];
          const updatedMessages = messages.map((message) =>
            message.id === updatedMessage.id ? updatedMessage : message
          );
          setMessages(updatedMessages);
        }
      } else if (data.coachMessages.messageUpdateType !== MessageUpdateType.STATUS_UPDATE) {
        dispatch(
          updateMessageStatus({
            messageStatusInput: ChatMessageStatus.DELIVERED,
            chatMessagesId: data.coachMessages.chatMessages[0].id,
          })
        );
      }
    }
  }, [data]);

  const fetchMoreData = () => {
    if (chatMessages.length >= totalMessageCount) {
      if (chatMessages.length !== 0 && totalMessageCount !== 0) {
        dispatch(setHasMoreMessages(false));
      }
      return;
    }
    const newSkip = skip + 10;
    dispatch(setMessagesSkip(newSkip));
    dispatch(getChatMessages({ coachId, clientId: client.id, skip: newSkip, take: 10 }));
  };

  const messagesByDate: { [key: string]: IChatMessage[] } = {};
  messages.forEach((message) => {
    const currentDate = message.createdAt.slice(0, 10);
    if (!messagesByDate[currentDate]) {
      messagesByDate[currentDate] = [];
    }
    messagesByDate[currentDate].push(message);
  });

  useEffect(
    () => () => {
      dispatch(clearChatMessages());
    },
    []
  );

  const baseHeight = isClientTab ? 253 : 213;
  const rowHeightAdjustment = rowCount > 5 ? 100 : rowCount * 22;
  const archivedHeightAdjustment = client.archived ? 80 : 0;

  const scrollContainerHeight = `calc(100vh - ${
    baseHeight + rowHeightAdjustment - archivedHeightAdjustment
  }px)`;

  return (
    <Grid display="flex" justifyContent="space-between" flexDirection="column">
      <Grid width="100%" display="flex" flexDirection="column">
        <InfiniteScroll
          dataLength={chatMessages.length}
          next={fetchMoreData}
          style={{
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: '12px',
            padding: `32px`,
            overflow: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: '#7CAEBE #fff', // thumb color track color
          }}
          inverse
          hasMore={hasMore}
          loader={
            <>
              <LoadingMessage own />
              <LoadingMessage own={false} />
            </>
          }
          height={scrollContainerHeight}
        >
          {getChatMessagesLoading && (
            <>
              <LoadingMessage own />
              <LoadingMessage own={false} />
              <LoadingMessage own />
              <LoadingMessage own={false} />
            </>
          )}
          {sendMessageLoading && <LoadingMessage own />}
          {Object.keys(messagesByDate).map((date) => (
            <React.Fragment key={date}>
              {messagesByDate[date].map((singleMessage) => (
                <Message
                  key={singleMessage.id}
                  message={singleMessage}
                  own={singleMessage.senderType === SenderType.COACH}
                  client={client}
                />
              ))}
              <Date messageDate={date} />
            </React.Fragment>
          ))}
        </InfiniteScroll>
      </Grid>
      {!client.archived && (
        <SendMessage setRowCount={setRowCount} clientId={client.id} isClientTab />
      )}
    </Grid>
  );
}

export default ChatBox;
